import { graphql } from "gatsby";
import { FluidObject } from "gatsby-image";
import { PostType, TopicKey } from "../topics";
import { PostSnippet, ProjectPart, ProjectSummary } from "../types";

export const fragments = graphql`
  fragment PostSnippet on MarkdownRemark {
    fields {
      slug
      readingTime
    }
    frontmatter {
      title
      description
      tags
      imgAlt
      publishedDate
      topic
      type
      img {
        childImageSharp {
          fluid(maxWidth: 800, maxHeight: 600, quality: 90) {
            ...GatsbyImageSharpFluid_withWebp
          }
        }
      }
    }
  }

  fragment ProjectSummary on MarkdownRemark {
    frontmatter {
      slug
      title
      topic
      summary
      imgAlt
      img {
        childImageSharp {
          fluid(maxWidth: 800, maxHeight: 400, quality: 90) {
            ...GatsbyImageSharpFluid_withWebp
          }
        }
      }
    }
  }

  fragment ProjectPart on MarkdownRemark {
    fields {
      slug
    }
    frontmatter {
      title
      projects {
        slug
        part
      }
    }
  }
`;

export interface PostSnippetNode {
  fields: { slug: string; readingTime: number };
  frontmatter: {
    title: string;
    description: string;
    tags: string[];
    imgAlt: string;
    publishedDate: string;
    topic: TopicKey;
    type: PostType;
    img: { childImageSharp: { fluid: FluidObject } };
  };
}

export interface ProjectSummaryNode {
  frontmatter: {
    slug: string;
    title: string;
    topic: TopicKey;
    summary: string;
    imgAlt: string;
    img: { childImageSharp: { fluid: FluidObject } };
  };
}

export interface ProjectPartNode {
  fields: { slug: string };
  frontmatter: {
    title: string;
    projects: { slug: string; part: number }[] | null;
  };
}

export const toPostSnippet = (node: PostSnippetNode): PostSnippet => ({
  title: node.frontmatter.title,
  summary: node.frontmatter.description,
  href: node.fields.slug,
  img: node.frontmatter.img.childImageSharp.fluid,
  imgAlt: node.frontmatter.imgAlt,
  tags: node.frontmatter.tags,
  publishedDate: new Date(node.frontmatter.publishedDate),
  topic: node.frontmatter.topic,
  type: node.frontmatter.type,
  readingTime: node.fields.readingTime,
});

/** Builds each project's ordered parts from every post that lists it. */
export const toProjectSummaries = (
  projects: ProjectSummaryNode[],
  partNodes: ProjectPartNode[]
): ProjectSummary[] =>
  projects.map(({ frontmatter }) => {
    const parts: ProjectPart[] = partNodes
      .flatMap((node) =>
        (node.frontmatter.projects || [])
          .filter((ref) => ref.slug === frontmatter.slug)
          .map((ref) => ({
            part: ref.part,
            title: node.frontmatter.title,
            href: node.fields.slug,
          }))
      )
      .sort((a, b) => a.part - b.part);
    return {
      slug: frontmatter.slug,
      href: `/projects/${frontmatter.slug}`,
      title: frontmatter.title,
      topic: frontmatter.topic,
      summary: frontmatter.summary,
      img: frontmatter.img.childImageSharp.fluid,
      imgAlt: frontmatter.imgAlt,
      parts,
    };
  });
